/**
 * Fechas de dashboard / admin (contrato §10 — unificación de promedios).
 *
 * | Endpoint | Formato |
 * |---|---|
 * | get-data (regular/seguro), admin-data | `m-d-Y` (+ hora opcional en admin-data) |
 * | admin-data/compare (regular/seguro) | `Y-m-d H:i:s` |
 * | all-values-totals, all-data*, memberships/dashboard | `Y-m-d` |
 *
 * Enviar otro formato no siempre falla: p. ej. en compare `10-09-2026` se lee
 * como día-mes y devuelve otro rango.
 * No se usa `new Date('texto')`: Safari no interpreta `m-d-Y` y `Y-m-d` se lee como UTC.
 */

const pad = n => String(n).padStart(2, '0')

/** Extrae hora `HH:mm:ss` si viene en el string; si no, null. */
function parseTimePart(value) {
  const text = String(value ?? '').trim()
  const match = /[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?/.exec(text)
  if (!match) return null
  return `${pad(+match[1])}:${pad(+match[2])}:${pad(+(match[3] || 0))}`
}

/** Acepta `Date`, `Y-m-d` o `m-d-Y` (con o sin ceros, con hora opcional). */
export function parseDateParts(value) {
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null
    return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() }
  }
  const text = String(value ?? '').trim().split(/[ T]/)[0]
  let match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text)
  if (match) return { year: +match[1], month: +match[2], day: +match[3] }
  match = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(text)
  if (match) return { year: +match[3], month: +match[1], day: +match[2] }
  return null
}

/** Fecha local a medianoche, o `null` si el valor no es una fecha válida. */
export function toLocalDate(value) {
  const parts = parseDateParts(value)
  return parts ? new Date(parts.year, parts.month - 1, parts.day) : null
}

export function formatYmd(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** `m-d-Y` — get-data / admin-data. Conserva hora si venía (`m-d-Y H:i:s`). */
export function toBackendDate(value) {
  const parts = parseDateParts(value)
  if (!parts) return value
  const date = `${pad(parts.month)}-${pad(parts.day)}-${parts.year}`
  const time = parseTimePart(value)
  return time ? `${date} ${time}` : date
}

/** `Y-m-d` — all-values-totals, all-data*, memberships. */
export function toYmdDate(value) {
  const parts = parseDateParts(value)
  return parts ? `${parts.year}-${pad(parts.month)}-${pad(parts.day)}` : value
}

/** `Y-m-d H:i:s` — admin-data/compare. Conserva hora si venía; si no, `00:00:00`. */
export function toCompareDate(value) {
  const parts = parseDateParts(value)
  if (!parts) return value
  const time = parseTimePart(value) || '00:00:00'
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)} ${time}`
}

function mapPayloadDates(payload, mapper) {
  const result = { ...payload }
  if ('start_date' in result) result.start_date = mapper(result.start_date)
  if ('end_date' in result) result.end_date = mapper(result.end_date)
  return result
}

/** Payload con fechas `m-d-Y` (get-data, admin-data). */
export function withBackendDates(payload = {}) {
  return mapPayloadDates(payload, toBackendDate)
}

/** Payload con fechas `Y-m-d` (all-values-totals, all-data*, memberships). */
export function withAllDataDates(payload = {}) {
  return mapPayloadDates(payload, toYmdDate)
}

/** Payload con fechas `Y-m-d H:i:s` (compare regular/seguro). */
export function withCompareDates(payload = {}) {
  return mapPayloadDates(payload, toCompareDate)
}

/** Diferencia en días entre dos fechas (fin - inicio), sin afectarse por zona horaria. */
export function daysBetween(start, end) {
  const s = parseDateParts(start)
  const e = parseDateParts(end)
  if (!s || !e) return 0
  const diff = Date.UTC(e.year, e.month - 1, e.day) - Date.UTC(s.year, s.month - 1, s.day)
  return Math.round(diff / 86400000)
}

/** Meses calendario que toca el rango, contando el primero y el último. */
export function monthsBetween(start, end) {
  const s = parseDateParts(start)
  const e = parseDateParts(end)
  if (!s || !e) return 0
  return (e.year - s.year) * 12 + (e.month - s.month) + 1
}
